import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "@/firebase";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  Building2,
  Users,
  Search,
  RefreshCw,
  Upload,
  UserPlus,
  ArrowLeft,
  Download,
  CheckCircle2,
  Star,
  StarOff,
  X,
  Loader2,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  Stethoscope,
  BarChart3,
  Sparkles,
} from "lucide-react";
import { calcCompletion, buildCsv, downloadCsv, isEmailDomainAllowed, generateSimulatedCohort } from "./helpers";
import AnalyticsTab from "./AnalyticsTab";

// Lazy import shared modals to avoid duplicating logic
import {
  AssociateUsersModal,
  UploadEmployeesModal,
  InviteCollaboratorModal,
} from "./SharedModals";

const CATEGORIES = {
  B2B: { label: "B2B", color: "#0284C7", bg: "#E0F2FE" },
  B2G: { label: "B2G", color: "#9333EA", bg: "#F3E8FF" },
};

export default function ClientConsole() {
  const { clientId, isClientAdmin, isAdmin, isDemo } = useUserProfile();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const targetClientId = searchParams.get("clientId") || clientId;
  const isAuthorized = isClientAdmin || isAdmin || isDemo;

  const [activeTab, setActiveTab] = useState("analytics"); // "analytics" | "employees"
  const [forceSimulated, setForceSimulated] = useState(false);
  const [client, setClient] = useState(null);
  const [employees, setEmployees] = useState([]); // raw user docs (with profile field)
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCompletion, setFilterCompletion] = useState("ALL");
  const [showAssociate, setShowAssociate] = useState(false);
  const [showAssociateProfessional, setShowAssociateProfessional] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [updating, setUpdating] = useState(null);

  const fetchData = useCallback(async () => {
    let effectiveClientId = targetClientId;
    setLoading(true);

    if (effectiveClientId === "demo-enterprise" || isDemo || (!effectiveClientId && currentUser?.isDemo)) {
      setClient({
        id: "demo-enterprise",
        name: "TechCorp Global B2B (Demostración)",
        category: "B2B",
        country: "Colombia",
        emailDomains: ["techcorp.com", "we360.com"],
      });
      setEmployees(generateSimulatedCohort(35));
      setLoading(false);
      return;
    }

    try {
      if (!effectiveClientId && isAdmin) {
        const clientsSnap = await getDocs(query(collection(db, "clients")));
        if (!clientsSnap.empty) {
          effectiveClientId = clientsSnap.docs[0].id;
        }
      }
      if (!effectiveClientId) {
        setLoading(false);
        return;
      }
      const [clientSnap, usersSnap] = await Promise.all([
        getDoc(doc(db, "clients", effectiveClientId)),
        getDocs(query(collection(db, "users"), where("clientId", "==", effectiveClientId))),
      ]);
      if (clientSnap.exists()) setClient({ id: clientSnap.id, ...clientSnap.data() });
      setEmployees(usersSnap.docs.map((d) => ({ uid: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error loading client console:", err);
    }
    setLoading(false);
  }, [targetClientId, isAdmin, isDemo, currentUser]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const simulatedCohort = useMemo(() => generateSimulatedCohort(35), []);
  const isClientDemoMode = isDemo || client?.id === "demo-enterprise";
  const effectiveEmployees = (forceSimulated && !isClientDemoMode) ? simulatedCohort : employees;

  const enriched = useMemo(() => {
    return effectiveEmployees.map((u) => {
      const completion = calcCompletion(u.profile);
      return {
        uid: u.uid,
        email: u.email || u.profile?.email || "",
        displayName: u.displayName || u.profile?.name || "",
        personalEmail: u.personalEmail || null,
        isActive: u.isActive !== false,
        clientRole: u.clientRole || "member",
        isIHO: u.isIHO === true,
        isMedico: u.isMedico === true || u.role === "medico",
        lastLogin: u.lastLogin,
        createdAt: u.createdAt,
        completion,
      };
    });
  }, [effectiveEmployees]);

  const stats = useMemo(() => {
    const total = enriched.length;
    const active = enriched.filter((e) => e.isActive).length;
    const fullyCompleted = enriched.filter((e) => e.completion.percent === 100).length;
    const inProgress = enriched.filter((e) => e.completion.percent > 0 && e.completion.percent < 100).length;
    return { total, active, fullyCompleted, inProgress };
  }, [enriched]);

  const hasIHO = useMemo(() => enriched.some((e) => e.isIHO), [enriched]);
  const hasMedico = useMemo(() => enriched.some((e) => e.isMedico), [enriched]);

  const filtered = useMemo(() => {
    return enriched.filter((e) => {
      if (filterCompletion === "FULL" && e.completion.percent !== 100) return false;
      if (filterCompletion === "PARTIAL" && (e.completion.percent === 0 || e.completion.percent === 100)) return false;
      if (filterCompletion === "NONE" && e.completion.percent !== 0) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        (e.email || "").toLowerCase().includes(s) ||
        (e.displayName || "").toLowerCase().includes(s)
      );
    });
  }, [enriched, search, filterCompletion]);

  const toggleClientAdmin = async (uid, currentRole) => {
    if (uid && String(uid).startsWith("sim-emp-")) return;
    setUpdating(uid);
    try {
      const newRole = currentRole === "admin" ? "member" : "admin";
      await updateDoc(doc(db, "users", uid), { clientRole: newRole });
      fetchData();
    } catch (err) { console.error(err); }
    setUpdating(null);
  };

  const toggleIHO = async (uid, currentIsIHO) => {
    if (uid && String(uid).startsWith("sim-emp-")) return;
    setUpdating(uid + "_iho");
    try {
      await updateDoc(doc(db, "users", uid), { isIHO: !currentIsIHO });
      setEmployees((prev) => prev.map((u) => u.uid === uid ? { ...u, isIHO: !currentIsIHO } : u));
    } catch (err) { console.error(err); }
    setUpdating(null);
  };

  const toggleMedico = async (uid, currentIsMedico) => {
    if (uid && String(uid).startsWith("sim-emp-")) return;
    setUpdating(uid + "_medico");
    const newIsMedico = !currentIsMedico;
    try {
      await updateDoc(doc(db, "users", uid), { isMedico: newIsMedico });
      setEmployees((prev) => prev.map((u) => u.uid === uid ? { ...u, isMedico: newIsMedico } : u));
    } catch (err) { console.error(err); }
    setUpdating(null);
  };

  const [unlinkWarning, setUnlinkWarning] = useState(null);
  // { uid, name, hasPersonalEmail, isSelf, blockedLastAdmin }

  const handleUnassign = async (uid) => {
    if (uid && String(uid).startsWith("sim-emp-")) return;
    const emp = enriched.find((e) => e.uid === uid);
    const hasPersonalEmail = !!(emp?.personalEmail);
    const isSelf = uid === currentUser?.uid;

    // Protección: si es el único admin, no puede quitarse a sí mismo
    if (isSelf && emp?.clientRole === "admin") {
      const adminCount = enriched.filter((e) => e.clientRole === "admin").length;
      if (adminCount <= 1) {
        setUnlinkWarning({
          uid, name: emp?.displayName || emp?.email || uid,
          hasPersonalEmail, isSelf, blockedLastAdmin: true,
        });
        return;
      }
    }

    // En todos los demás casos: pedir confirmación siempre
    setUnlinkWarning({
      uid, name: emp?.displayName || emp?.email || uid,
      hasPersonalEmail, isSelf: isSelf || false, blockedLastAdmin: false,
    });
  };

  const doUnlink = async (uid) => {
    if (uid && String(uid).startsWith("sim-emp-")) return;
    setUpdating(uid);
    setUnlinkWarning(null);
    try {
      await updateDoc(doc(db, "users", uid), { clientId: null, clientRole: "member" });
      fetchData();
    } catch (err) { console.error(err); }
    setUpdating(null);
  };

  const exportCsv = () => {
    const headers = [
      "Email",
      "Nombre",
      "Rol en empresa",
      "Estado",
      "% Completado",
      "F1",
      "F2",
      "F3",
      "F4",
      "F5",
      "Último acceso",
    ];
    const rows = enriched.map((e) => [
      e.email,
      e.displayName,
      e.clientRole === "admin" ? "Admin" : "Colaborador",
      e.isActive ? "Activo" : "Inactivo",
      `${e.completion.percent}%`,
      e.completion.completed.f1 ? "Sí" : "No",
      e.completion.completed.f2 ? "Sí" : "No",
      e.completion.completed.f3 ? "Sí" : "No",
      e.completion.completed.f4 ? "Sí" : "No",
      e.completion.completed.f5 ? "Sí" : "No",
      e.lastLogin ? new Date(e.lastLogin).toLocaleDateString("es-CO") : "—",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${c}"`).join(","))].join("\n");
    const safeName = (client?.name || "empresa").toLowerCase().replace(/[^a-z0-9]/g, "_");
    const prefix = forceSimulated ? "demo_proyeccion_" : "";
    downloadCsv(`colaboradores_${prefix}${safeName}_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  // ─── Permission gate ─────────────────────────────────────
  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#FCFAEE" }}>
        <div className="we-card p-6 max-w-md text-center" data-testid="client-console-forbidden">
          <ShieldCheck size={32} className="mx-auto mb-3" style={{ color: "#C0453A" }} />
          <h2 className="text-base font-bold mb-2" style={{ color: "#1E293B" }}>Acceso restringido</h2>
          <p className="text-sm mb-4" style={{ color: "#64748B" }}>
            Esta consola es solo para administradores asignados de un cliente empresarial.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer"
            style={{ background: "#0284C7", color: "white", border: "none" }}
            data-testid="client-back-dashboard"
          >
            Volver al dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F1F5F9" }}>
        <Loader2 size={32} className="animate-spin" style={{ color: "#0284C7" }} />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#FCFAEE" }}>
        <div className="we-card p-6 max-w-md text-center">
          <p className="text-sm" style={{ color: "#64748B" }}>
            No se encontró tu empresa. Contacta a soporte WE360.
          </p>
        </div>
      </div>
    );
  }

  const cs = CATEGORIES[client.category] || { label: client.category, color: "#64748B", bg: "#F1F5F9" };
  const allowedDomains = client.emailDomains || [];

  return (
    <div className="min-h-screen" style={{ background: "#F1F5F9" }} data-testid="client-console">
      {/* Header */}
      <header className="bg-white border-b" style={{ borderColor: "#E2E8F0" }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 rounded-lg hover:bg-gray-100 cursor-pointer shrink-0"
              style={{ border: "none", background: "none" }}
              data-testid="client-back-btn"
            >
              <ArrowLeft size={18} style={{ color: "#475569" }} />
            </button>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: cs.bg }}>
              <Building2 size={20} style={{ color: cs.color }} />
            </div>
            <div className="min-w-0 notranslate" translate="no">
              <h1 className="text-lg font-bold truncate notranslate" translate="no" style={{ color: "#1E293B" }} data-testid="client-name">
                {client.name}
              </h1>
              <div className="flex items-center gap-2 notranslate" translate="no">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full notranslate" translate="no" style={{ background: cs.bg, color: cs.color }}>
                  {cs.label}
                </span>
                {client.country && <span className="text-[11px] notranslate" translate="no" style={{ color: "#94A3B8" }}>{client.country}</span>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={exportCsv}
              disabled={enriched.length === 0}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold cursor-pointer transition-opacity hover:opacity-90"
              style={{ background: "#F1F5F9", color: "#475569", border: "1px solid #CBD5E1", opacity: enriched.length === 0 ? 0.5 : 1 }}
              data-testid="client-export-csv"
            >
              <Download size={14} />
              Exportar CSV
            </button>
            <button
              onClick={() => setShowAssociate(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold cursor-pointer"
              style={{ background: "#F1F5F9", color: "#0284C7", border: "1px solid #0284C7" }}
              data-testid="client-associate-btn"
            >
              <UserPlus size={14} />
              Asociar usuarios
            </button>
            <button
              onClick={() => setShowAssociateProfessional(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold cursor-pointer"
              style={{ background: "#F1F5F9", color: "#0D9488", border: "1px solid #0D9488" }}
              data-testid="client-associate-professional-btn"
            >
              <Stethoscope size={14} />
              Asociar Médico/IHO Externo
            </button>
            <button
              onClick={() => setShowInvite(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold cursor-pointer text-white shadow-sm"
              style={{ background: "#00628F" }}
              data-testid="client-invite-btn"
            >
              <UserPlus size={14} />
              Invitar Colaborador
            </button>
            <button
              onClick={() => setShowUpload(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold cursor-pointer"
              style={{ background: "#0284C7", color: "white", border: "none" }}
              data-testid="client-upload-btn"
            >
              <Upload size={14} />
              Cargar CSV
            </button>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Navigation Tabs */}
        <div className="flex items-center gap-2 mb-6 bg-white p-1.5 rounded-2xl border border-slate-200 shadow-2xs max-w-fit">
          <button
            type="button"
            onClick={() => setActiveTab("analytics")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              activeTab === "analytics"
                ? "bg-teal-700 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            }`}
            data-testid="tab-analytics-btn"
          >
            <BarChart3 size={15} />
            Analítica de Salud & Riesgo Poblacional
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("employees")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              activeTab === "employees"
                ? "bg-teal-700 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            }`}
            data-testid="tab-employees-btn"
          >
            <Users size={15} />
            Directorio de Colaboradores ({stats.total}{forceSimulated ? " · Demo" : ""})
          </button>
        </div>

        {activeTab === "analytics" ? (
          <AnalyticsTab
            client={client}
            employees={employees}
            forceSimulated={forceSimulated}
            onToggleSimulated={setForceSimulated}
          />
        ) : (
          <div>
            {forceSimulated && !isClientDemoMode && (
              <div
                className="mb-4 p-3.5 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-between gap-3 flex-wrap text-amber-900"
                data-testid="demo-cohort-banner"
              >
                <div className="flex items-center gap-2.5">
                  <Sparkles size={18} className="text-amber-600 shrink-0" />
                  <span className="text-xs font-medium">
                    <strong>Modo Proyección Activo:</strong> Visualizando cohorte demostrativa de 35 colaboradores con 80% de participación (28 completaron 5/5). Los datos reales de su empresa ({employees.length} registros) permanecen protegidos.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setForceSimulated(false)}
                  className="px-3 py-1 rounded-lg text-xs font-semibold bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 cursor-pointer shadow-2xs transition-colors shrink-0"
                  data-testid="exit-demo-mode-btn"
                >
                  Volver a datos reales ({employees.length})
                </button>
              </div>
            )}
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <StatTile label="Total colaboradores" value={stats.total} color="#0284C7" bg="#E0F2FE" testId="stat-total" />
              <StatTile label="Activos" value={stats.active} color="#16A34A" bg="#DCFCE7" testId="stat-active" />
              <StatTile label="Completaron 5/5" value={stats.fullyCompleted} color="#9333EA" bg="#F3E8FF" testId="stat-full" />
              <StatTile label="En progreso" value={stats.inProgress} color="#F9A01B" bg="#FFF3DC" testId="stat-progress" />
            </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} />
            <input
              type="text"
              placeholder="Buscar por email o nombre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm"
              style={{ borderColor: "#E2E8F0", background: "white", color: "#1E293B" }}
              data-testid="client-search"
            />
          </div>
          <select
            value={filterCompletion}
            onChange={(e) => setFilterCompletion(e.target.value)}
            className="px-3 py-2.5 rounded-lg border text-sm cursor-pointer"
            style={{ borderColor: "#E2E8F0", background: "white", color: "#1E293B" }}
            data-testid="client-completion-filter"
          >
            <option value="ALL">Todos</option>
            <option value="FULL">Completaron 5/5</option>
            <option value="PARTIAL">En progreso</option>
            <option value="NONE">Sin iniciar</option>
          </select>
          <button
            onClick={fetchData}
            className="p-2.5 rounded-lg border hover:bg-gray-50"
            style={{ borderColor: "#E2E8F0" }}
            data-testid="client-refresh"
          >
            <RefreshCw size={16} style={{ color: "#64748B" }} />
          </button>
          {!isClientDemoMode && !forceSimulated && (
            <button
              type="button"
              onClick={() => setForceSimulated(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 cursor-pointer transition-colors"
              data-testid="enter-demo-directory-btn"
              title="Previsualizar cohorte proyectada de 35 colaboradores"
            >
              <Sparkles size={13} className="text-amber-600" />
              Previsualizar Proyección (35)
            </button>
          )}
        </div>

        {/* Privacy notice */}
        <div className="mb-4 p-3 rounded-lg text-xs flex items-start gap-2" style={{ background: "#FEF3C7", color: "#92400E" }}>
          <ShieldCheck size={14} className="shrink-0 mt-0.5" />
          <span>
            <strong>Privacidad:</strong> solo ves información administrativa de tus colaboradores (email, nombre, % de evaluaciones). Los datos clínicos personales (peso, presión, condiciones, etc.) son privados y solo accesibles por el propio colaborador.
          </span>
        </div>

        {/* Cobertura Clínica Alerts */}
        {(!hasIHO || !hasMedico) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-testid="client-coverage-warnings">
            {!hasIHO && (
              <div className="p-3.5 rounded-xl border flex items-start gap-3 bg-blue-50/50" style={{ borderColor: "#BFDBFE", color: "#1E3A8A" }}>
                <ShieldAlert size={18} className="shrink-0 mt-0.5 text-blue-600 animate-pulse" />
                <div>
                  <h4 className="text-xs font-bold">Falta Oficial de Salud (IHO)</h4>
                  <p className="text-[11px] leading-relaxed opacity-90 mt-0.5">
                    Esta empresa no cuenta con un Oficial de Salud (IHO). Los IHO coordinan las alertas de riesgo y autorizan reportes en la plataforma.
                  </p>
                </div>
              </div>
            )}
            {!hasMedico && (
              <div className="p-3.5 rounded-xl border flex items-start gap-3 bg-emerald-50/50" style={{ borderColor: "#A7F3D0", color: "#064E3B" }}>
                <Stethoscope size={18} className="shrink-0 mt-0.5 text-emerald-600 animate-pulse" />
                <div>
                  <h4 className="text-xs font-bold">Falta Médico de Familia</h4>
                  <p className="text-[11px] leading-relaxed opacity-90 mt-0.5">
                    Esta empresa no tiene un médico de cabecera asignado. El médico es indispensable para supervisar y diagnosticar las alertas clínicas OMS.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="client-employees-table">
              <thead>
                <tr style={{ background: "#F8FAFC" }}>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: "#475569" }}>Colaborador</th>
                  <th className="text-center px-4 py-3 font-semibold" style={{ color: "#475569" }}>% Completado</th>
                  <th className="text-center px-4 py-3 font-semibold" style={{ color: "#475569" }}>Forms</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: "#475569" }}>Último acceso</th>
                  <th className="text-center px-4 py-3 font-semibold" style={{ color: "#475569" }}>Rol</th>
                  <th className="text-center px-4 py-3 font-semibold" style={{ color: "#475569" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.uid} className="border-t hover:bg-gray-50/50" style={{ borderColor: "#F1F5F9" }} data-testid={`client-row-${e.uid}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium" style={{ color: "#1E293B" }}>{e.displayName || "—"}</div>
                      <div className="text-[11px]" style={{ color: "#94A3B8" }}>{e.email}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <CompletionBar percent={e.completion.percent} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <FormDots completed={e.completion.completed} />
                    </td>
                    <td className="px-4 py-3" style={{ color: "#64748B" }}>
                      {e.lastLogin ? new Date(e.lastLogin).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        {e.clientRole === "admin" ? (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }}>
                            Admin
                          </span>
                        ) : (
                          <span className="text-[10px]" style={{ color: "#94A3B8" }}>Colaborador</span>
                        )}
                        {e.isIHO && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{ background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE" }}
                            data-testid={`client-iho-badge-${e.uid}`}>
                            <ShieldAlert size={9} />
                            IHO
                          </span>
                        )}
                        {e.isMedico && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{ background: "#F0FDF4", color: "#065F46", border: "1px solid #A7F3D0" }}
                            data-testid={`client-medico-badge-${e.uid}`}>
                            <Stethoscope size={9} />
                            Médico
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => toggleClientAdmin(e.uid, e.clientRole)}
                          disabled={updating === e.uid}
                          title={e.clientRole === "admin" ? "Quitar admin" : "Hacer admin"}
                          className="p-1.5 rounded-md hover:bg-yellow-50 cursor-pointer"
                          style={{ border: "none", background: "none" }}
                          data-testid={`client-toggle-admin-${e.uid}`}
                        >
                          {updating === e.uid ? (
                            <Loader2 size={14} className="animate-spin" style={{ color: "#94A3B8" }} />
                          ) : e.clientRole === "admin" ? (
                            <StarOff size={14} style={{ color: "#D97706" }} />
                          ) : (
                            <Star size={14} style={{ color: "#94A3B8" }} />
                          )}
                        </button>
                        <button
                          onClick={() => toggleIHO(e.uid, e.isIHO)}
                          disabled={updating === e.uid + "_iho"}
                          title={e.isIHO ? "Quitar rol IHO" : "Asignar como IHO (Oficial de Salud)"}
                          className="p-1.5 rounded-md hover:bg-blue-50 cursor-pointer"
                          style={{ border: "none", background: "none" }}
                          data-testid={`client-toggle-iho-${e.uid}`}
                        >
                          {updating === e.uid + "_iho" ? (
                            <Loader2 size={14} className="animate-spin" style={{ color: "#94A3B8" }} />
                          ) : (
                            <ShieldAlert size={14} style={{ color: e.isIHO ? "#1D4ED8" : "#CBD5E1" }} />
                          )}
                        </button>
                        <button
                          onClick={() => toggleMedico(e.uid, e.isMedico)}
                          disabled={updating === e.uid + "_medico"}
                          title={e.isMedico ? "Quitar rol Médico de Familia" : "Asignar como Médico de Familia"}
                          className="p-1.5 rounded-md hover:bg-emerald-50 cursor-pointer"
                          style={{ border: "none", background: "none" }}
                          data-testid={`client-toggle-medico-${e.uid}`}
                        >
                          {updating === e.uid + "_medico" ? (
                            <Loader2 size={14} className="animate-spin" style={{ color: "#94A3B8" }} />
                          ) : (
                            <Stethoscope size={14} style={{ color: e.isMedico ? "#059669" : "#CBD5E1" }} />
                          )}
                        </button>
                        <button
                          onClick={() => handleUnassign(e.uid)}
                          disabled={updating === e.uid}
                          title="Quitar de la empresa"
                          className="p-1.5 rounded-md hover:bg-red-50 cursor-pointer"
                          style={{ border: "none", background: "none" }}
                          data-testid={`client-unassign-${e.uid}`}
                        >
                          <X size={14} style={{ color: "#DC2626" }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr key="empty-state-row">
                    <td colSpan={6} className="text-center py-12" style={{ color: "#94A3B8" }}>
                      {employees.length === 0
                        ? "Aún no tienes colaboradores asignados. Usa los botones de arriba para asociar o cargar colaboradores."
                        : "No hay colaboradores que coincidan con los filtros."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )}
  </div>

      {/* Modals */}
      {showAssociate && (
        <AssociateUsersModal
          client={client}
          allUsers={null /* signal: client-admin mode, fetch B2C from Firestore */}
          domainRestricted
          allowedDomains={allowedDomains}
          onClose={() => setShowAssociate(false)}
          onCompleted={() => { setShowAssociate(false); fetchData(); }}
        />
      )}
      {showAssociateProfessional && (
        <AssociateUsersModal
          client={client}
          allUsers={null}
          ignoreDomainRestriction={true}
          professionalsOnly={true}
          onClose={() => setShowAssociateProfessional(false)}
          onCompleted={() => { setShowAssociateProfessional(false); fetchData(); }}
        />
      )}
      {showUpload && (
        <UploadEmployeesModal
          client={client}
          users={[]}
          clients={[client]}
          domainRestricted
          allowedDomains={allowedDomains}
          onClose={() => setShowUpload(false)}
          onCompleted={() => { setShowUpload(false); fetchData(); }}
        />
      )}
      {showInvite && (
        <InviteCollaboratorModal
          client={client}
          allowedDomains={allowedDomains}
          onClose={() => setShowInvite(false)}
          onCompleted={() => { setShowInvite(false); fetchData(); }}
        />
      )}

      {/* ── Modal: confirmación antes de desvincular ── */}
      {unlinkWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6" data-testid="unlink-warning-modal">

            {/* CASO 1: Bloqueado — único admin */}
            {unlinkWarning.blockedLastAdmin ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "#FEE2E2" }}>
                    <AlertTriangle size={20} style={{ color: "#DC2626" }} />
                  </div>
                  <h3 className="font-bold" style={{ color: "#1E293B" }}>No puedes quitarte</h3>
                </div>
                <p className="text-sm mb-2" style={{ color: "#475569" }}>
                  Eres el <strong>único administrador</strong> de la empresa. Si te quitas, nadie podría gestionar la consola.
                </p>
                <p className="text-sm mb-5" style={{ color: "#475569" }}>
                  Asigna otro administrador primero usando el ícono <strong>⭐</strong> en la fila de otro colaborador.
                </p>
                <button
                  onClick={() => setUnlinkWarning(null)}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold"
                  style={{ background: "#00628F", color: "white", border: "none", cursor: "pointer" }}
                  data-testid="unlink-blocked-ok"
                >
                  Entendido
                </button>
              </>
            ) : (
              /* CASO 2: Auto-remoción permitida o remoción normal */
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: unlinkWarning.hasPersonalEmail ? "#FEE2E2" : "#FEF3C7" }}
                  >
                    <AlertTriangle size={20} style={{ color: unlinkWarning.hasPersonalEmail ? "#DC2626" : "#D97706" }} />
                  </div>
                  <h3 className="font-bold" style={{ color: "#1E293B" }}>
                    {unlinkWarning.isSelf ? "¿Salir de la empresa?" : "¿Quitar de la empresa?"}
                  </h3>
                </div>

                {unlinkWarning.isSelf ? (
                  <p className="text-sm mb-3" style={{ color: "#475569" }}>
                    Estás a punto de <strong>quitarte a ti mismo</strong> de la empresa. Tu perfil de salud personal se mantendrá intacto.
                  </p>
                ) : (
                  <p className="text-sm mb-3" style={{ color: "#475569" }}>
                    Estás a punto de quitar a <strong>{unlinkWarning.name}</strong> de la empresa. Su perfil de salud personal se mantendrá intacto.
                  </p>
                )}

                {!unlinkWarning.hasPersonalEmail && (
                  <div
                    className="text-xs mb-4 px-3 py-2.5 rounded-xl"
                    style={{ background: "#FFFBEB", border: "1px solid #FCD34D", color: "#92400E" }}
                  >
                    <strong>Atención:</strong> {unlinkWarning.isSelf ? "No tienes" : "Esta persona no tiene"} email personal de respaldo. Si el acceso corporativo se corta, <strong>se perderá el acceso permanente a los datos de salud</strong>.
                  </div>
                )}

                <div className="space-y-2">
                  <button
                    onClick={() => setUnlinkWarning(null)}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold border"
                    style={{ borderColor: "#E2E8F0", color: "#475569" }}
                    data-testid="unlink-warning-cancel"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => doUnlink(unlinkWarning.uid)}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold"
                    style={{ background: "#DC2626", color: "white", border: "none", cursor: "pointer" }}
                    data-testid="unlink-warning-confirm"
                  >
                    {unlinkWarning.isSelf ? "Sí, salir de la empresa" : "Sí, quitar de la empresa"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────
function StatTile({ label, value, color, bg, testId }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-100" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: bg }}>
          <Users size={20} style={{ color }} />
        </div>
        <div>
          <p className="text-2xl font-bold" style={{ color: "#1E293B" }}>{value}</p>
          <p className="text-xs" style={{ color: "#64748B" }}>{label}</p>
        </div>
      </div>
    </div>
  );
}

function CompletionBar({ percent }) {
  const color = percent === 100 ? "#16A34A" : percent >= 60 ? "#0284C7" : percent >= 20 ? "#F9A01B" : "#94A3B8";
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 rounded-full overflow-hidden" style={{ background: "#F1F5F9", height: 6 }}>
        <div style={{ background: color, width: `${percent}%`, height: "100%", transition: "width 0.3s" }} />
      </div>
      <span className="text-xs font-bold w-9 text-right" style={{ color }}>{percent}%</span>
    </div>
  );
}

function FormDots({ completed }) {
  const items = [
    { key: "f1", label: "F1" },
    { key: "f2", label: "F2" },
    { key: "f3", label: "F3" },
    { key: "f4", label: "F4" },
    { key: "f5", label: "F5" },
  ];
  return (
    <div className="flex items-center gap-1 justify-center">
      {items.map((it) => (
        <span
          key={it.key}
          title={`${it.label}: ${completed[it.key] ? "completado" : "pendiente"}`}
          className="w-5 h-5 rounded text-[9px] font-bold flex items-center justify-center"
          style={completed[it.key]
            ? { background: "#DCFCE7", color: "#166534" }
            : { background: "#F1F5F9", color: "#94A3B8" }
          }
        >
          {completed[it.key] ? <CheckCircle2 size={10} /> : it.label.slice(1)}
        </span>
      ))}
    </div>
  );
}
